import { useQuery } from '@tanstack/react-query'
import { useLocalSearchParams } from 'expo-router'
import { useState } from 'react'
import { Linking, Platform } from 'react-native'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { api } from '../../src/api/client'
import { PdfViewer } from '../../src/features/edo/components/PdfViewer'
import { SigningTicket } from '../../src/features/signing/components/SigningTicket'
import { signingVM } from '../../src/features/signing/signingVM'
import { useApp } from '../../src/i18n'
import { useDocumentTitle } from '../../src/lib/useDocumentTitle'
import { tone } from '../../src/theme/tones'
import { Button, Card, SkeletonLines } from '../../src/ui'
import { Page } from '../../src/ui/Screen'

/** Пока подпись ждут, статус меняется на телефоне подписанта — опрашиваем раз в 5 секунд. */
const LIVE = 5_000

/**
 * Публичная страница подписания `/s/<токен>` — ею договор подписывает КОНТРАГЕНТ: человек без
 * учётной записи в портале (ФТ-25…ФТ-27 ТЗ SIMA, сценарий С2).
 *
 * Поэтому здесь нет ничего портального: ни навигации, ни профиля, ни запросов к защищённым
 * ручкам — только одна публичная ручка, пропуск к которой лежит в самом токене ссылки.
 * `AuthGate` этот путь не трогает (`isPublicPath`), иначе контрагента уводило бы на логин,
 * куда ему входить нечем.
 *
 * Негодный, истёкший и чужой токен сервер отдаёт одинаково — 404; страница так и говорит:
 * «ссылка больше не действует». Отличать «нет такой» от «протухла» наружу незачем.
 */
export default function PublicSigningScreen() {
  const { t } = useApp()
  const { token } = useLocalSearchParams<{ token?: string }>()
  const [preview, setPreview] = useState(Platform.OS === 'web')
  const q = useQuery({
    queryKey: ['public-signing', token],
    queryFn: () => api.publicSigning(token as string),
    enabled: Boolean(token),
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'created' || status === 'data_served' ? LIVE : false
    },
  })
  const card = q.data
  useDocumentTitle(t({ ru: 'Подписание документа', en: 'Document signing', az: 'Sənədin imzalanması' }))

  if (q.isLoading) {
    return (
      <Page maxWidth={620}>
        <SkeletonLines lines={4} />
      </Page>
    )
  }

  if (!card) {
    return (
      <Page maxWidth={620} title={t({ ru: 'Подписание документа', en: 'Document signing', az: 'Sənədin imzalanması' })}>
        <Card padding={20}>
          <Text fontSize={14} color="$muted" lineHeight={22}>
            {t({
              ru: 'Эта ссылка больше не действует. Запросите новую у отправителя документа.',
              en: 'This link is no longer valid. Ask the sender for a new one.',
              az: 'Bu keçid artıq etibarlı deyil. Göndərəndən yenisini istəyin.',
            })}
          </Text>
        </Card>
      </Page>
    )
  }

  const vm = signingVM(card)
  const documentUrl = api.publicSigningDocumentUrl(token as string)
  const name = card.documentName ?? t({ ru: 'Документ', en: 'Document', az: 'Sənəd' })

  return (
    <Page maxWidth={620}>
      <Card gap={16} padding={20}>
        <XStack gap={12} alignItems="center" justifyContent="space-between">
          <YStack gap={4} flex={1}>
            <Text fontSize={17} fontWeight="700" color="$color" numberOfLines={3}>
              {name}
            </Text>
            <Text fontSize={13} color="$muted">
              {t({ ru: 'Отправитель', en: 'Sender', az: 'Göndərən' })}: {card.sender}
            </Text>
          </YStack>
          <Stack paddingHorizontal={10} paddingVertical={4} borderRadius={999} backgroundColor={tone(vm.tone).soft}>
            <Text fontSize={12} fontWeight="600" color={tone(vm.tone).text}>
              {t(vm.statusLabel)}
            </Text>
          </Stack>
        </XStack>

        {vm.canShowQr && vm.link ? (
          <YStack gap={10} alignItems="center">
            <SigningTicket link={vm.link} />
            <Text fontSize={13} color="$muted" textAlign="center">
              {t({
                ru: 'Откройте SIMA İmza → встроенный сканер → выберите сертификат организации → PIN',
                en: 'Open SIMA İmza → built-in scanner → pick the organisation certificate → PIN',
                az: 'SIMA İmza açın → daxili skaner → təşkilatın sertifikatını seçin → PIN',
              })}
            </Text>
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Ссылка действует до', en: 'The link is valid until', az: 'Keçid bu tarixə qədər etibarlıdır' })}:{' '}
              {new Date(card.expUtc * 1000).toLocaleString()}
            </Text>
          </YStack>
        ) : null}

        {/* Предпросмотр ниже талона: главное действие на странице — подписать, документ
            читают вторым шагом. На вебе iframe, на телефоне — системный просмотрщик. */}
        {preview && Platform.OS === 'web' ? (
          <PdfViewer src={documentUrl} title={name} onClose={() => setPreview(false)} />
        ) : (
          <Button onPress={() => void Linking.openURL(documentUrl)}>
            {t({ ru: 'Посмотреть документ', en: 'View the document', az: 'Sənədə bax' })}
          </Button>
        )}

        {card.status === 'completed' ? (
          <YStack gap={8}>
            <Text fontSize={13} color={tone(card.pdfCheck?.ok ? 'green' : 'slate').text}>
              {t(vm.signatures)}
            </Text>
            {/* Свой подписанный экземпляр контрагент забирает по этой же ссылке (ФТ-27);
                исходный документ она после подписи уже не отдаёт. */}
            <Button onPress={() => void Linking.openURL(documentUrl)}>
              {t({ ru: 'Скачать подписанный документ', en: 'Download the signed document', az: 'İmzalanmış sənədi yüklə' })}
            </Button>
          </YStack>
        ) : null}

        {vm.expired && card.status !== 'completed' ? (
          <Text fontSize={13} color="$muted" lineHeight={20}>
            {t({
              ru: 'Срок подписания истёк. Запросите новую ссылку у отправителя документа.',
              en: 'The signing period has ended. Ask the sender for a new link.',
              az: 'İmzalama müddəti bitdi. Göndərəndən yeni keçid istəyin.',
            })}
          </Text>
        ) : null}
      </Card>
    </Page>
  )
}
